import { Paragraph, Text } from "@theme/components";

import {
  AccordionItem,
  AccordionPanel,
  AccordionRoot,
  AccordionTrigger,
} from "@theme/components/primitives/Accordion";

import type { ThemeUIStyleObject } from "@theme/components";
import type { ElementType, FC, ReactNode } from "react";

export interface AccordionProps {
  title: string | ReactNode;
  type?: "TEXT" | "SLOT";
  defaultExpanded?: boolean;
  sx?: ThemeUIStyleObject;
  className?: string;
  headingLevel?: 2 | 3 | 4 | 5 | 6;
  /** When the item children has too much content and need to overwrite the paragraph tag */
  as?: ElementType;
  children: ReactNode;
}

// Fixed Base UI item name — each Accordion is a single-item root, so it never collides.
const ACCORDION_ITEM_VALUE = "item";

export const Accordion: FC<AccordionProps> = ({
  title,
  type = "TEXT",
  defaultExpanded = false,
  children,
  sx,
  className,
  headingLevel,
  as,
}) => {
  const accordionTitle =
    typeof title === "string" ? (
      <Text
        as="p"
        variant="sansBody02MediumPlus"
        sx={{
          transition: "color .3s ease",
          color: "textPrimary",
        }}
      >
        {title}
      </Text>
    ) : (
      title
    );

  return (
    <AccordionRoot defaultValue={defaultExpanded ? [ACCORDION_ITEM_VALUE] : []}>
      <AccordionItem value={ACCORDION_ITEM_VALUE} className={className} sx={sx}>
        <AccordionTrigger headingLevel={headingLevel}>{accordionTitle}</AccordionTrigger>

        <AccordionPanel>
          <Paragraph
            data-slot="accordion-content"
            as={as ?? (type === "TEXT" ? "p" : "div")}
            variant={type === "TEXT" ? "sansBody01Regular" : undefined}
            sx={{
              maxWidth: "content05",
              pb: "s400",
              outlineColor: "backgroundBrand",
              outlineWidth: "2px",
              ...(type === "TEXT" && {
                textAlign: "left",
                color: "textLevel3",
                "p:last-of-type": {
                  mb: 0,
                },
              }),
            }}
          >
            {children}
          </Paragraph>
        </AccordionPanel>
      </AccordionItem>
    </AccordionRoot>
  );
};
